import React, { useState } from 'react';
import {
  Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  List, ListItem, ListItemText, Typography,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';

// Multi-select for an unmapped-values queue: the admin ticks rows that carry an AI suggestion
// and maps them all to that suggestion in one go (Daniel, 2026-10-22). Each row goes through
// the same single-mapping endpoint as the "Map" button, one request at a time, so a row that
// fails (e.g. its suggestion names a type that no longer exists) is reported and the rest
// still land.
//
// rows: the page's rows; selected: Set of row ids; canSelect(row): whether a row is selectable
// (has a suggestion, no conflict); labelOf(row) / suggestionOf(row): strings for the list;
// mapOne(row): Promise, maps one row; onDone(): reload.
export function canMapToSuggestion(row) {
  return Boolean(row.suggested_normalized_value) && row.review_status !== 'conflict_multiple_suggestions';
}

export function SelectAllCheckbox({ rows, selected, setSelected, canSelect }) {
  const selectable = rows.filter(canSelect);
  const allOn = selectable.length > 0 && selectable.every(r => selected.has(r.id));
  const someOn = selectable.some(r => selected.has(r.id));
  return (
    <Checkbox
      size="small"
      checked={allOn}
      indeterminate={someOn && !allOn}
      disabled={selectable.length === 0}
      onChange={(e) => setSelected(e.target.checked ? new Set(selectable.map(r => r.id)) : new Set())}
      inputProps={{ 'aria-label': 'select every row on this page that has an AI suggestion' }}
    />
  );
}

export function RowCheckbox({ row, selected, setSelected, canSelect }) {
  const ok = canSelect(row);
  return (
    <Checkbox
      size="small"
      checked={selected.has(row.id)}
      disabled={!ok}
      onChange={(e) => {
        const next = new Set(selected);
        if (e.target.checked) next.add(row.id); else next.delete(row.id);
        setSelected(next);
      }}
      inputProps={{ 'aria-label': ok ? 'select for mapping to the AI suggestion' : 'no AI suggestion to map to' }}
    />
  );
}

export default function BulkMapBar({ rows, selected, setSelected, labelOf, suggestionOf, mapOne, onDone }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  const chosen = rows.filter(r => selected.has(r.id));
  if (chosen.length === 0 && !result) return null;

  const run = async () => {
    setRunning(true);
    setProgress(0);
    const failed = [];
    let done = 0;
    for (const row of chosen) {
      try {
        await mapOne(row);
      } catch (e) {
        failed.push(`${labelOf(row)} → ${suggestionOf(row)}: ${e.message || 'failed'}`);
      }
      done += 1;
      setProgress(done);
    }
    setRunning(false);
    setConfirmOpen(false);
    setSelected(new Set());
    setResult({ mapped: chosen.length - failed.length, failed });
    onDone();
  };

  return (
    <>
      {result && (
        <Alert severity={result.failed.length ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setResult(null)}>
          Mapped {result.mapped} label(s) to their AI suggestion.
          {result.failed.length > 0 && (
            <List dense disablePadding sx={{ mt: 1 }}>
              {result.failed.map(f => <ListItem key={f} disableGutters><ListItemText primary={f} /></ListItem>)}
            </List>
          )}
        </Alert>
      )}
      {chosen.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" startIcon={<DoneAllIcon />} onClick={() => setConfirmOpen(true)}>
              Map {chosen.length} to AI suggestion
            </Button>
          }
        >
          {chosen.length} label(s) selected.
        </Alert>
      )}
      <Dialog open={confirmOpen} onClose={() => !running && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Map {chosen.length} label(s) to their AI suggestion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Each label is mapped exactly as the AI suggested. Untick a row first if you want a different type for it.
          </Typography>
          <List dense>
            {chosen.map(r => (
              <ListItem key={r.id} disableGutters>
                <ListItemText primary={`${labelOf(r)} → ${suggestionOf(r)}`} />
              </ListItem>
            ))}
          </List>
          {running && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <CircularProgress size={14} sx={{ mr: 1 }} /> {progress} of {chosen.length}…
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={running}>Cancel</Button>
          <Button onClick={run} variant="contained" disabled={running}>Map all</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
